import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home, Utensils, ShoppingBag, Stethoscope, Briefcase, Sparkles, Users,
  MessageSquareQuote, HelpCircle, Newspaper, Check, ArrowRight, ArrowLeft, Boxes,
  Scissors, GraduationCap, Dumbbell, PawPrint, Wrench, HardHat, Compass, Calendar, Scale,
  type LucideIcon,
} from 'lucide-react';
import {
  getBodyFontOption,
  getBrandingSuggestion,
  getDisplayFontOption,
} from '@palacete/shared/branding';
import { INDUSTRIES } from '@palacete/shared/presets';
import { api } from '../../lib/api';
import type { OnboardingCatalog, IndustryOption, PresetSummary } from '../../lib/types';
import { pickLoc } from '../../lib/collections';
import { PublicPanelShell } from '../../components/ui/PublicPanelShell';
import VideroMark from '../../components/ui/VideroMark';

interface Props {
  onComplete: () => void;
}

const ICONS: Record<string, LucideIcon> = {
  home: Home, utensils: Utensils, 'shopping-bag': ShoppingBag, stethoscope: Stethoscope,
  briefcase: Briefcase, sparkles: Sparkles, users: Users,
  'message-square-quote': MessageSquareQuote, 'help-circle': HelpCircle, newspaper: Newspaper,
  scissors: Scissors, 'graduation-cap': GraduationCap, dumbbell: Dumbbell,
  'paw-print': PawPrint, wrench: Wrench, 'hard-hat': HardHat, compass: Compass,
  calendar: Calendar, scale: Scale,
};
const iconFor = (name: string): LucideIcon => ICONS[name] ?? Boxes;

type Step = 'industry' | 'types' | 'seeding' | 'building';
type SeedStatus = 'pending' | 'running' | 'done';

const fade = {
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -14 },
  transition: { duration: 0.32, ease: 'easeOut' as const },
};

export default function OnboardingWizard({ onComplete }: Props) {
  const [catalog, setCatalog] = useState<OnboardingCatalog | null>(null);
  const [step, setStep] = useState<Step>('industry');
  const [industry, setIndustry] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [description, setDescription] = useState('');
  const [seedStatus, setSeedStatus] = useState<Record<string, SeedStatus>>({});
  const [error, setError] = useState('');

  useEffect(() => {
    api.onboarding.catalog().then(setCatalog).catch((e) => setError((e as Error).message));
  }, []);

  // Presets sugeridos para la industria elegida (en orden) + universales no repetidos.
  // Usa INDUSTRIES local para no depender del API para datos estáticos.
  const suggestedKeys = useMemo(() => {
    if (!catalog || !industry) return [];
    const ind = INDUSTRIES.find((i) => i.key === industry);
    const base = ind?.recommended ?? [];
    const extra = catalog.universal.filter((k) => !base.includes(k));
    return [...base, ...extra];
  }, [catalog, industry]);
  const brandingSuggestion = useMemo(() => getBrandingSuggestion(industry), [industry]);
  const brandingDisplayFont = getDisplayFontOption(brandingSuggestion.theme.fontDisplay);
  const brandingBodyFont = getBodyFontOption(brandingSuggestion.theme.fontBody);

  function chooseIndustry(opt: IndustryOption) {
    setIndustry(opt.key);
    setSelected(new Set(opt.recommended));
    setStep('types');
  }

  function toggle(key: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }

  async function runSeeding() {
    if (!industry || selected.size === 0) return;
    setStep('seeding');
    setError('');
    const keys = suggestedKeys.filter((k) => selected.has(k));
    setSeedStatus(Object.fromEntries(keys.map((k) => [k, 'pending'])) as Record<string, SeedStatus>);
    try {
      await api.onboarding.instantiate(keys, industry);
      // Siembra de contenido inicial con IA, tipo a tipo (con progreso visible).
      for (const key of keys) {
        setSeedStatus((s) => ({ ...s, [key]: 'running' }));
        await seedType(key);
        setSeedStatus((s) => ({ ...s, [key]: 'done' }));
      }
      // Construye las páginas del sitio (Home + secciones + contacto + nav) con IA.
      // No rompe el onboarding si falla: el usuario tendrá sus colecciones igual.
      setStep('building');
      try {
        await api.ai.site(description || undefined);
        // Fase 4: encola las imágenes de los bloques auto-elegibles. No esperamos
        // a que terminen: el sitio se "termina de vestir" mientras el usuario
        // entra al panel. Si falla (sin imagen habilitada, cupo, etc.) no rompe nada.
        api.ai.siteImages().catch(() => { /* el sitio queda con sus placeholders */ });
      } catch {
        /* sin API key / límite: el sitio queda sin armar, el usuario lo arma luego */
      }
      await api.onboarding.complete();
      // Pequeña pausa para que se vea el último check antes de entrar al panel.
      setTimeout(onComplete, 600);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  // Siembra LLM de un tipo: genera e inserta sus primeros ítems. Si la generación
  // falla (sin API key, límite, etc.) no rompe el onboarding: el tipo queda creado
  // y vacío para que el usuario lo llene a mano.
  async function seedType(key: string) {
    try {
      await api.ai.seed(key, 3, description || undefined);
    } catch {
      /* el tipo queda vacío; el usuario puede generar/añadir luego */
    }
  }

  if (error && !catalog) {
    return (
      <div className="min-h-screen bg-ui-bg flex items-center justify-center p-6">
        <p className="text-sm text-ui-danger">{error}</p>
      </div>
    );
  }

  return (
    <PublicPanelShell widthClassName="max-w-4xl">
      {/* Progreso por pasos */}
      {(step === 'industry' || step === 'types') ? (
        <div className="flex items-center gap-2 mb-8">
          {(['industry', 'types', 'seeding', 'building'] as Step[]).map((s, i) => {
            const active = step === s;
            const passed = (['industry', 'types', 'seeding', 'building'] as Step[]).indexOf(step) > i;
            return (
              <motion.div
                key={s}
                animate={{ width: active ? 28 : 8 }}
                className={`h-2 rounded-full ${active || passed ? 'bg-ui-accent' : 'bg-ui-text/15'}`}
              />
            );
          })}
        </div>
      ) : null}

      <div className="w-full max-w-2xl">
        <AnimatePresence mode="wait">
          {step === 'industry' && (
            <motion.div key="industry" {...fade}>
              <div className="text-center mb-8 space-y-2">
                <motion.div
                  initial={{ scale: 0.6, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 18 }}
                  className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-ui-accent text-ui-text-inverse shadow-lg shadow-ui-accent/20"
                >
                  <VideroMark className="h-8 w-8" />
                </motion.div>
                <h1 className="text-2xl font-semibold text-ui-text">¿A qué se dedica tu negocio?</h1>
                <p className="text-sm text-ui-text-muted">Elige lo más parecido. Preparamos tu sitio con lo esencial para ti.</p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {catalog?.industries.map((opt, i) => {
                  const Icon = iconFor(opt.icon);
                  return (
                    <motion.button
                      key={opt.key}
                      type="button"
                      onClick={() => chooseIndustry(opt)}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.05 * i }}
                      whileHover={{ y: -3 }}
                      whileTap={{ scale: 0.97 }}
                      className="text-left p-4 rounded-xl bg-ui-surface border border-ui-border hover:border-ui-accent/50 transition-colors"
                    >
                      <Icon className="w-5 h-5 text-ui-accent mb-2" />
                      <p className="text-sm font-medium text-ui-text">{pickLoc(opt.label, 'es')}</p>
                      <p className="text-sm text-ui-text-subtle mt-1 leading-snug">{pickLoc(opt.blurb, 'es')}</p>
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          )}

          {step === 'types' && catalog && (
            <motion.div key="types" {...fade}>
              <div className="text-center mb-6 space-y-2">
                <motion.div
                  initial={{ scale: 0.6, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 18 }}
                  className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-ui-accent text-ui-text-inverse shadow-lg shadow-ui-accent/20"
                >
                  <VideroMark className="h-8 w-8" />
                </motion.div>
                <h1 className="text-2xl font-semibold text-ui-text">¿Qué quieres gestionar?</h1>
                <p className="text-sm text-ui-text-muted">
                  Activa lo que necesites. Cada uno es una sección de tu sitio que podrás editar siempre.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                {suggestedKeys.map((key, i) => {
                  const preset = catalog.presets[key];
                  if (!preset) return null;
                  return (
                    <PresetCard
                      key={key}
                      preset={preset}
                      industry={industry}
                      selected={selected.has(key)}
                      onToggle={() => toggle(key)}
                      delay={0.04 * i}
                    />
                  );
                })}
              </div>

              <div className="mb-5 rounded-xl border border-ui-border bg-ui-surface p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-ui-text">Branding sugerido para tu giro</p>
                    <p className="text-sm text-ui-text-subtle">
                      Arrancarás con la paleta <span className="text-ui-text">{brandingSuggestion.paletteName}</span>
                      {' '}y una combinación tipográfica pensada para este tipo de negocio.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <span className="h-8 w-8 rounded-lg border border-ui-border" style={{ backgroundColor: brandingSuggestion.theme.accent }} />
                    <span className="h-8 w-8 rounded-lg border border-ui-border" style={{ backgroundColor: brandingSuggestion.theme.deepAlt }} />
                  </div>
                </div>
                <p className="text-sm text-ui-text-subtle">{brandingSuggestion.rationale}</p>
                <div className="grid gap-2 sm:grid-cols-2">
                  <div className="rounded-lg border border-ui-border bg-ui-bg px-3 py-2">
                    <p className="text-sm text-ui-text-subtle">Títulos</p>
                    <p className="text-sm font-medium text-ui-text">{brandingDisplayFont?.label ?? 'Catálogo soportado'}</p>
                  </div>
                  <div className="rounded-lg border border-ui-border bg-ui-bg px-3 py-2">
                    <p className="text-sm text-ui-text-subtle">Texto</p>
                    <p className="text-sm font-medium text-ui-text">{brandingBodyFont?.label ?? 'Catálogo soportado'}</p>
                  </div>
                </div>
                <p className="text-sm text-ui-text-subtle">
                  Después podrás cambiar colores y fuentes desde Apariencia, incluso pidiéndole a la IA un ajuste más específico.
                </p>
              </div>

              <div className="mb-6">
                <label className="block text-sm text-ui-text-muted mb-1.5">
                  Cuéntanos de tu negocio <span className="text-ui-text-subtle">(opcional — mejora el contenido que generamos)</span>
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  placeholder="Ej. Inmobiliaria boutique en Monterrey especializada en residencias de lujo y atención personalizada."
                  className="w-full bg-ui-bg border border-ui-border rounded-lg px-3 py-2 text-sm text-ui-text placeholder-ui-text-subtle focus:outline-none focus:border-ui-accent resize-none"
                />
              </div>

              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setStep('industry')}
                  className="flex items-center gap-1.5 text-sm text-ui-text-muted hover:text-ui-text transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" /> Atrás
                </button>
                <motion.button
                  type="button"
                  onClick={runSeeding}
                  disabled={selected.size === 0}
                  whileTap={{ scale: 0.97 }}
                  className="flex items-center gap-2 px-5 py-2.5 bg-ui-accent text-ui-text-inverse text-sm font-medium rounded-lg disabled:opacity-40 hover:opacity-90 transition-opacity"
                >
                  Crear mi sitio <ArrowRight className="w-4 h-4" />
                </motion.button>
              </div>
            </motion.div>
          )}

          {step === 'seeding' && catalog && (
            <motion.div key="seeding" {...fade} className="text-center">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 2.4, ease: 'linear' }}
                className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-ui-accent text-ui-text-inverse shadow-lg shadow-ui-accent/20"
              >
                <VideroMark className="h-8 w-8" />
              </motion.div>
              <h1 className="text-2xl font-semibold text-ui-text mb-1">Preparando tu sitio</h1>
              <p className="text-sm text-ui-text-muted mb-8">Estamos creando tus secciones y su primer contenido.</p>

              <div className="max-w-sm mx-auto space-y-2 text-left">
                {Object.keys(seedStatus).map((key) => {
                  const preset = catalog.presets[key];
                  const status = seedStatus[key];
                  return (
                    <motion.div
                      key={key}
                      layout
                      className="flex items-center gap-3 p-3 rounded-lg bg-ui-surface border border-ui-border"
                    >
                      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        {status === 'done' ? (
                          <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 400, damping: 15 }}>
                            <Check className="w-4 h-4 text-ui-accent" />
                          </motion.span>
                        ) : status === 'running' ? (
                          <span className="w-3.5 h-3.5 border-2 border-ui-accent border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <span className="w-2 h-2 rounded-full bg-ui-text-subtle" />
                        )}
                      </span>
                      <span className={`text-sm ${status === 'pending' ? 'text-ui-text-subtle' : 'text-ui-text'}`}>
                        {preset ? pickLoc(preset.labels.plural, 'es') : key}
                      </span>
                    </motion.div>
                  );
                })}
              </div>

              {error && <p className="text-sm text-ui-danger mt-6">{error}</p>}
            </motion.div>
          )}

          {step === 'building' && (
            <motion.div key="building" {...fade} className="text-center">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 2.4, ease: 'linear' }}
                className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-ui-accent text-ui-text-inverse shadow-lg shadow-ui-accent/20"
              >
                <VideroMark className="h-8 w-8" />
              </motion.div>
              <h1 className="text-2xl font-semibold text-ui-text mb-1">Armando tu sitio</h1>
              <p className="text-sm text-ui-text-muted mb-8">
                Estamos creando tu página de inicio, tus secciones y tu menú de navegación.
              </p>
              <div className="flex justify-center">
                <span className="w-5 h-5 border-2 border-ui-accent border-t-transparent rounded-full animate-spin" />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </PublicPanelShell>
  );
}

function PresetCard({ preset, industry, selected, onToggle, delay }: {
  preset: PresetSummary; industry: string | null; selected: boolean; onToggle: () => void; delay: number;
}) {
  const Icon = iconFor(preset.icon);
  const previewFields = preset.fields.slice(0, 4);

  const ind = industry ? INDUSTRIES.find((i) => i.key === industry) : null;
  const override = ind?.labelOverrides?.[preset.key];
  const label = override ? override.plural.es : pickLoc(preset.labels.plural, 'es');

  return (
    <motion.button
      type="button"
      onClick={onToggle}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.98 }}
      className={`relative text-left p-4 rounded-xl border transition-colors ${
        selected
          ? 'bg-ui-accent/10 border-ui-accent/50'
          : 'bg-ui-surface border-ui-border hover:border-ui-border-strong'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <Icon className={`w-5 h-5 ${selected ? 'text-ui-accent' : 'text-ui-text-muted'}`} />
        <motion.span
          animate={{ scale: selected ? 1 : 0, opacity: selected ? 1 : 0 }}
          className="w-5 h-5 rounded-full bg-ui-accent flex items-center justify-center"
        >
          <Check className="w-3.5 h-3.5 text-ui-bg" />
        </motion.span>
      </div>
      <p className="text-sm font-medium text-ui-text mt-2">{label}</p>
      <div className="flex flex-wrap gap-1 mt-2">
        {previewFields.map((f, i) => (
          <span key={i} className="text-xs text-ui-text-subtle bg-ui-text/[0.05] rounded px-1.5 py-0.5">
            {pickLoc(f.label, 'es')}
          </span>
        ))}
      </div>
    </motion.button>
  );
}
